import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { customerService } from '../services/customer.service';
import { creditService } from '../services/credit.service';
import {
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CustomerQueryParams,
} from '../types/customer.types';

export const useCustomersPage = (params: CustomerQueryParams) => {
  return useQuery({
    queryKey: ['customers', 'page', params],
    queryFn: () => customerService.getPage(params),
    placeholderData: keepPreviousData,
  });
};

export const useCustomerSummary = () => {
  return useQuery({
    queryKey: ['customers', 'summary'],
    queryFn: () => customerService.getSummary(),
  });
};

// Credit limit, outstanding amount and open orders of one customer
export const useCustomerCreditInfo = (customerId: string | null) => {
  return useQuery({
    queryKey: ['customers', 'credit-info', customerId],
    queryFn: () => creditService.getCustomerCreditInfo(customerId as string),
    enabled: !!customerId,
  });
};

// Active customers for dropdowns, in the shape the forms already use
export const useActiveCustomers = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['customers', 'active'],
    queryFn: () => customerService.getActive(),
  });

  return {
    customers: data ?? [],
    loading: isLoading,
    error: error ? 'Failed to fetch active customers' : null,
  };
};

const useInvalidateCustomers = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['customers'] });
};

export const useCreateCustomer = () => {
  const invalidate = useInvalidateCustomers();
  return useMutation({
    mutationFn: (data: CreateCustomerRequest) => customerService.create(data),
    onSuccess: invalidate,
  });
};

export const useUpdateCustomer = () => {
  const invalidate = useInvalidateCustomers();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCustomerRequest }) =>
      customerService.update(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteCustomer = () => {
  const invalidate = useInvalidateCustomers();
  return useMutation({
    mutationFn: (id: string) => customerService.delete(id),
    onSuccess: invalidate,
  });
};
